import { InjectionToken, WritableSignal, signal } from '@angular/core';

/** The non-standard event Chrome fires when the PWA is installable. Not in the TS DOM lib. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** Holds the most recent captured install event and the installed flag. Chrome can fire
 *  `beforeinstallprompt` before Angular bootstraps, so main.ts writes here from a pre-bootstrap
 *  listener and the service reads it. Signals so the service reacts to a late-arriving event. */
export interface InstallPromptSource {
  event: WritableSignal<BeforeInstallPromptEvent | null>;
  installed: WritableSignal<boolean>;
}

/** Module-level singleton the pre-bootstrap listener and the DI token share. */
const holder: InstallPromptSource = {
  event: signal<BeforeInstallPromptEvent | null>(null),
  installed: signal(false),
};

export const INSTALL_PROMPT_SOURCE = new InjectionToken<InstallPromptSource>(
  'INSTALL_PROMPT_SOURCE',
  { providedIn: 'root', factory: () => holder },
);

let registered = false;

/** Register the window listeners once, from main.ts, before bootstrap. */
export function captureInstallEvents(target: Window = window): void {
  if (registered) return;
  registered = true;
  target.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault(); // suppress Chrome's mini-infobar; we drive our own button
    holder.event.set(e as BeforeInstallPromptEvent);
  });
  target.addEventListener('appinstalled', () => {
    holder.installed.set(true);
    holder.event.set(null);
  });
}
